import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';
import { CloudOff, RefreshCw, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui';
import { Logo } from '@/components/Logo';
import { applyServiceWorkerUpdate, useOnlineStatus } from '@/lib/pwa';
import { cn } from '@/lib/cn';

/**
 * The last line of defence.
 *
 * A render error in React 19 unmounts the whole tree, so without this a single bad listing — a
 * malformed date, a null the types promised could not be null — turns the entire site into a blank
 * white page with the real story in a console nobody is reading. A class component because error
 * boundaries have no hook equivalent and, seven years on, still do not.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Nothing is sent anywhere yet, but the console entry is deliberately kept: a blank page with a
    // silent boundary is harder to diagnose than a blank page without one.
    console.error('Unhandled render error', error, info.componentStack);
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <div className="mx-auto flex min-h-[60dvh] max-w-md flex-col items-center justify-center gap-4 px-4 text-center">
        <Logo className="h-9 opacity-70" title="KojaKola" />

        <span className="flex size-14 items-center justify-center rounded-2xl bg-danger/12 text-danger">
          <TriangleAlert size={26} />
        </span>

        <h1 className="text-xl font-bold text-body">Nešto je pošlo naopako</h1>

        <p className="text-sm leading-relaxed text-muted">
          Stranica se nije učitala kako treba. Osvežavanje najčešće pomaže — ako se ponovi, javite
          nam na podrska@kojakola.com.
        </p>

        <div className="flex flex-wrap justify-center gap-2">
          <Button onClick={() => window.location.reload()}>
            <RefreshCw size={16} />
            Osveži stranicu
          </Button>
          <Button variant="secondary" onClick={() => (window.location.href = '/')}>
            Nazad na početnu
          </Button>
        </div>
      </div>
    );
  }
}

/**
 * A strip that says why nothing is loading.
 *
 * It appears on the way down and stays for a moment after reconnecting, because "you are back
 * online" is information too — otherwise a visitor who lost signal in a lift has no idea whether
 * the empty page is the network or the site.
 */
export function ConnectionBanner() {
  const online = useOnlineStatus();
  const [showRestored, setShowRestored] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (!online) {
      setWasOffline(true);
      return;
    }

    if (!wasOffline) {
      return;
    }

    setShowRestored(true);
    const timer = window.setTimeout(() => {
      setShowRestored(false);
      setWasOffline(false);
    }, 2600);

    return () => window.clearTimeout(timer);
  }, [online, wasOffline]);

  if (online && !showRestored) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'animate-fade-in-down fixed inset-x-0 top-0 z-[60] flex items-center justify-center gap-2 px-4 py-2 text-xs font-medium',
        online ? 'bg-success text-white' : 'bg-danger text-white',
      )}
    >
      <CloudOff size={13} />
      {online ? 'Veza je uspostavljena.' : 'Nema internet veze — prikazujemo sačuvane podatke.'}
    </div>
  );
}

/**
 * "There is a new version."
 *
 * Deliberately not automatic. A page that reloads itself under someone halfway through typing a
 * listing is a page that lost their listing.
 */
export function UpdateBanner({ visible }: { visible: boolean }) {
  if (!visible) {
    return null;
  }

  return (
    <div className="animate-fade-in-up fixed inset-x-3 bottom-[calc(var(--tab-bar-total)_+_0.75rem)] z-[60] mx-auto flex max-w-sm items-center gap-3 rounded-2xl border border-default bg-raised p-3 shadow-float sm:bottom-4">
      <RefreshCw size={16} className="shrink-0 text-accent" />
      <span className="min-w-0 flex-1 text-[13px] text-body">Dostupna je nova verzija sajta.</span>
      <Button size="sm" onClick={applyServiceWorkerUpdate}>
        Osveži
      </Button>
    </div>
  );
}
